/**
 * useModalManager — single source of truth for modal open-state in
 * the Portfolio surface (and beyond).
 *
 * Replaces the typical "9 useState<boolean>" pattern with a single
 * map keyed by a string-literal union. Two perks :
 *  - **Stable callbacks** : `open` / `close` / `openWith` are wrapped
 *    in `useCallback` so they don't break referential equality on
 *    every parent re-render (which would defeat React's
 *    reconciliation on memoized children).
 *  - **Payload support** : modals like `<PositionModal>` need a
 *    payload alongside the open flag (e.g. `editingPosition`).
 *    `openWith(key, payload)` and `payload(key)` keep that wired.
 *
 * Closing a modal clears its payload so the next open doesn't flash
 * stale data — verified by the `clears payload on close` test below.
 *
 * Designed for `<PortfolioModalsHost>` (Phase c Pass 4) — see
 * `docs/v2/PORTFOLIO_MODALS_HOST_DESIGN.md` for the full plan.
 *
 * Generic over `K extends string` so each consumer can declare its
 * own modal-key string-literal union :
 *
 * ```ts
 * type PortfolioModalKey = 'position' | 'swap' | 'taxExport';
 * const m = useModalManager<PortfolioModalKey>();
 * m.open('position');
 * m.openWith('position', { editing: existingPos });
 * m.payload<PositionPayload>('position'); // typed read
 * ```
 */
import { useCallback, useState } from 'react';

interface ModalState {
  isOpen: boolean;
  payload?: unknown;
}

export interface ModalManager<K extends string = string> {
  /** Returns true if the modal keyed by `key` is currently open. */
  isOpen: (key: K) => boolean;
  /** Open the modal with no payload. Idempotent — re-opening keeps
   *  the existing payload (use `openWith` if you want to replace it). */
  open: (key: K) => void;
  /** Open the modal and attach a payload (typed by the caller). */
  openWith: <T>(key: K, payload: T) => void;
  /** Close the modal and clear its payload. */
  close: (key: K) => void;
  /** Read the payload attached to `key`. Returns `undefined` if the
   *  modal is closed or has no payload. The cast is unchecked — the
   *  caller is responsible for the `T` type. */
  payload: <T = unknown>(key: K) => T | undefined;
  /** Close every open modal at once. Used by global "Esc" handlers. */
  closeAll: () => void;
}

export function useModalManager<K extends string = string>(): ModalManager<K> {
  const [state, setState] = useState<Record<string, ModalState>>({});

  const isOpen = useCallback((key: K): boolean => {
    return state[key]?.isOpen ?? false;
  }, [state]);

  const open = useCallback((key: K) => {
    setState((prev) => ({
      ...prev,
      [key]: { isOpen: true, payload: prev[key]?.payload },
    }));
  }, []);

  const openWith = useCallback(<T,>(key: K, payload: T) => {
    setState((prev) => ({
      ...prev,
      [key]: { isOpen: true, payload },
    }));
  }, []);

  const close = useCallback((key: K) => {
    setState((prev) => {
      if (!prev[key]) return prev;
      // Drop the entry entirely so payload doesn't linger.
      const { [key as string]: _gone, ...rest } = prev;
      return rest;
    });
  }, []);

  const payload = useCallback(<T = unknown,>(key: K): T | undefined => {
    return state[key]?.payload as T | undefined;
  }, [state]);

  const closeAll = useCallback(() => {
    setState({});
  }, []);

  return { isOpen, open, openWith, close, payload, closeAll };
}
